import React, { useEffect, useMemo, useRef, useId } from 'react';

const POINT_NUM = 20;

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
const lerp = (a, b, t) => a + (b - a) * t;

const hashString = (value) => {
  let hash = 2166136261;
  for (let i = 0; i < value.length; i += 1) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
};

const createSeededRandom = (seed) => {
  let state = seed >>> 0;
  return () => {
    state = (Math.imul(state, 1664525) + 1013904223) >>> 0;
    return state / 4294967296;
  };
};

const sampleLevel = (analyser, scratch) => {
  if (!analyser || !scratch) return 0;
  analyser.getByteTimeDomainData(scratch);

  let sum = 0;
  for (let i = 0; i < scratch.length; i += 1) {
    const value = (scratch[i] - 128) / 128;
    sum += value * value;
  }

  return clamp(Math.sqrt(sum / scratch.length) * 3.1, 0, 1);
};

const SiriGlowOrb = ({ phase = 'idle', size = 144, analyser = null, volume = null }) => {
  const rawId = useId().replace(/:/g, '');
  const rootRef = useRef(null);
  const levelRef = useRef(0);
  const morphRef = useRef(0);
  const frameRef = useRef(0);
  const scale = size / 200;

  const points = useMemo(() => {
    const random = createSeededRandom(hashString(`${rawId}-${size}`));
    return Array.from({ length: POINT_NUM }, () => ({
      xFrames: Array.from({ length: 5 }, () => Math.round((-100 + random() * 400) * scale)),
      yFrames: Array.from({ length: 5 }, () => Math.round((-100 + random() * 400) * scale)),
      color: [
        Math.floor(random() * 255),
        Math.floor(random() * 255),
        Math.floor(random() * 255),
      ],
      alpha: Number((0.5 + random()).toFixed(3)),
      originX: Math.round(random() * 255 * scale),
      originY: Math.round(random() * 255 * scale),
    }));
  }, [rawId, scale, size]);

  const keyframes = useMemo(() => {
    const ballXName = `voiceBallX-${rawId}`;
    const ballYName = `voiceBallY-${rawId}`;
    const pointStyles = points.map((point, index) => {
      const pointIndex = index + 1;
      const xName = `voicePointX-${rawId}-${pointIndex}`;
      const yName = `voicePointY-${rawId}-${pointIndex}`;
      return `
        @keyframes ${xName} {
          0% { transform: translateX(${point.xFrames[0]}px); }
          25% { transform: translateX(${point.xFrames[1]}px); }
          50% { transform: translateX(${point.xFrames[2]}px); }
          75% { transform: translateX(${point.xFrames[3]}px); }
          100% { transform: translateX(${point.xFrames[4]}px); }
        }
        @keyframes ${yName} {
          0% { transform: translateY(${point.yFrames[0]}px); }
          25% { transform: translateY(${point.yFrames[1]}px); }
          50% { transform: translateY(${point.yFrames[2]}px); }
          75% { transform: translateY(${point.yFrames[3]}px); }
          100% { transform: translateY(${point.yFrames[4]}px); }
        }
      `;
    }).join('\n');

    return `
      @keyframes ${ballXName} {
        0% { transform: translateX(${300 * scale}px); }
        50% { transform: translateX(${100 * scale}px); }
        100% { transform: translateX(${300 * scale}px); }
      }
      @keyframes ${ballYName} {
        0% { transform: translateY(${-100 * scale}px); }
        50% { transform: translateY(${100 * scale}px); }
        100% { transform: translateY(${300 * scale}px); }
      }
      ${pointStyles}
    `;
  }, [points, rawId, scale]);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;

    const scratch = analyser ? new Uint8Array(analyser.fftSize || 512) : null;

    const tick = (now) => {
      const targetMorph = phase === 'idle' ? 0 : 1;
      morphRef.current = lerp(morphRef.current, targetMorph, targetMorph > morphRef.current ? 0.12 : 0.06);

      const syntheticLevel =
        phase === 'speaking'
          ? 0.34 + Math.sin(now * 0.01) * 0.08
          : phase === 'processing'
            ? 0.24 + Math.sin(now * 0.008) * 0.04
            : phase === 'listening'
              ? 0.18 + Math.sin(now * 0.006) * 0.03
              : 0.08;

      const rawLevel = volume ?? (analyser ? sampleLevel(analyser, scratch) : syntheticLevel);
      levelRef.current = lerp(levelRef.current, rawLevel, rawLevel > levelRef.current ? 0.18 : 0.08);

      const level = levelRef.current;
      const morph = morphRef.current;
      root.style.setProperty('--orb-brightness', (1 + morph * 0.08 + level * 0.16).toFixed(4));
      root.style.setProperty('--orb-saturate', (1.02 + morph * 0.18 + level * 0.28).toFixed(4));
      root.style.setProperty('--orb-scale', (1 + morph * 0.015 + level * 0.035).toFixed(4));
      root.style.setProperty('--orb-glow-opacity', (0.82 + morph * 0.08 + level * 0.14).toFixed(4));

      frameRef.current = requestAnimationFrame(tick);
    };

    frameRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frameRef.current);
  }, [analyser, phase, volume]);

  return (
    <div
      ref={rootRef}
      className="siri-orb"
      style={{ width: size, height: size }}
      aria-hidden="true"
    >
      <style>{`
        ${keyframes}

        .siri-orb {
          --orb-brightness: 1;
          --orb-saturate: 1.02;
          --orb-scale: 1;
          --orb-glow-opacity: 0.84;
          position: relative;
          border-radius: 9999px;
          overflow: hidden;
          background: rgb(108, 173, 238);
          transform: scale(var(--orb-scale));
          filter: saturate(var(--orb-saturate)) brightness(var(--orb-brightness));
          isolation: isolate;
        }
        .orb-inner,
        .point-out,
        .bg {
          position: absolute;
          inset: 0;
          border-radius: inherit;
        }
        .orb-inner {
          overflow: hidden;
        }
        .point-in,
        .bg2 {
          position: absolute;
          top: 0;
          left: 0;
          width: 0;
          height: 0;
        }
        .bg {
          animation: voiceBallY-${rawId} 5s linear infinite;
          opacity: var(--orb-glow-opacity);
        }
        .bg2 {
          box-shadow: 0 0 ${60 * scale}px ${50 * scale}px rgb(255, 60, 0);
          animation: voiceBallX-${rawId} 5s cubic-bezier(0.55, 0, 0.4, 1.01) infinite;
        }
      `}</style>

      <div className="orb-inner">
        <div className="bg">
          <div className="bg2" />
        </div>

        {points.map((point, index) => {
          const pointIndex = index + 1;
          const xName = `voicePointX-${rawId}-${pointIndex}`;
          const yName = `voicePointY-${rawId}-${pointIndex}`;

          return (
            <div
              key={`${rawId}-${pointIndex}`}
              className="point-out"
              style={{ animation: `${xName} 8s linear infinite alternate` }}
            >
              <div
                className="point-in"
                style={{
                  boxShadow: `0 0 ${70 * scale}px ${80 * scale}px rgba(${point.color[0]}, ${point.color[1]}, ${point.color[2]}, ${point.alpha})`,
                  transform: `translate(${point.originX}px, ${point.originY}px)`,
                  animation: `${yName} 8s cubic-bezier(0.55, 0, 0.4, 1.01) infinite alternate`,
                }}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SiriGlowOrb;
